import { MenuItem } from "@mui/material";
import { Formik, Form } from "formik";
import { ReactElement } from "react";
import { FormattedMessage } from "react-intl";
import * as Yup from "yup";

import {
  EditOwnProfileMutation,
  EditOwnProfileMutationVariables,
  SupportedLanguage,
  UserTenantRole,
} from "~frontend/__generated__/graphql";
import PageContainer from "~frontend/components/PageContainer";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { EDIT_OWN_PROFILE } from "~frontend/graphql/users";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import {
  detectBrowserLanguage,
  useIntl,
} from "~frontend/providers/IntlProvider";
import { useCurrentUser } from "~frontend/providers/LoggedInUserProvider";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

const languageLabels: Record<SupportedLanguage, string> = {
  EN_US: "English",
  ES: "Español",
  XX: "XX (Testing)",
};

export function Profile(): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();
  const intl = useIntl();
  const user = useCurrentUser()!;

  useBreadcrumbs([
    {
      label: intl.formatMessage({
        defaultMessage: "User Profile",
        description: "User profile page header",
      }),
      path: "/user/profile",
    },
  ]);

  const [editOwnProfile] = useMutation<
    EditOwnProfileMutation,
    EditOwnProfileMutationVariables
  >(EDIT_OWN_PROFILE, {
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to update user profile",
      description: "Error message when updating user profile fails",
    }),
  });

  const preferredLanguageSelectionEnabled = useFeatureFlag(
    "preferredLanguageSelection"
  );

  return (
    <PageContainer maxWidth="lg">
      <Formik
        initialValues={{
          [submitErrorField]: null,
          fullName: user.fullName,
          language: user.language || "",
        }}
        validationSchema={Yup.object().shape({
          fullName: Yup.string().required(
            intl.formatMessage({
              defaultMessage: "Full name is required",
              description: "Error message when full name is not provided",
            })
          ),
        })}
        onSubmit={async (values, formik) => {
          const { errors, data } = await editOwnProfile({
            variables: {
              input: {
                fullName: values.fullName,
                language: (values.language as SupportedLanguage) || null,
              },
            },
          });

          if (errors) {
            formik.setErrors({
              [submitErrorField]: getErrorMessage(errors),
            });
          } else if (data) {
            formik.resetForm({
              values: {
                [submitErrorField]: null,
                fullName: data!.editOwnProfile.fullName,
                language: data!.editOwnProfile.language || "",
              },
            });
          }
        }}
      >
        <Form>
          <FormikErrorBanner />
          <FormikTextField
            name="fullName"
            label={
              <FormattedMessage
                defaultMessage="Full Name"
                description="Full name user profile field input label"
              />
            }
            fullWidth
            inputProps={{ "data-testid": "edit-profile-name-field" }}
          />
          {preferredLanguageSelectionEnabled && (
            <FormikSelect
              name="language"
              label={
                <FormattedMessage
                  defaultMessage="Preferred Language"
                  description="Label to select user's preferred language"
                />
              }
              fullWidth
            >
              <MenuItem value="">
                <FormattedMessage
                  defaultMessage="Browser Default ({browserDefaultLanguageName})"
                  description="Label for menu option to use the web browser's default language"
                  values={{
                    browserDefaultLanguageName:
                      languageLabels[detectBrowserLanguage()],
                  }}
                />
              </MenuItem>
              {Object.entries(languageLabels)
                .filter(
                  ([value]) =>
                    value !== "XX" || user.tenantRole === UserTenantRole.Staff
                )
                .map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
            </FormikSelect>
          )}
          <FormikSubmitButton
            color="primary"
            variant="contained"
            submitText={intl.formatMessage({
              defaultMessage: "Save",
              description: "Save button text",
            })}
            loadingText={intl.formatMessage({
              defaultMessage: "Saving...",
              description: "Save button text while loading",
            })}
            data-testid="save-profile-basics-button"
          />
        </Form>
      </Formik>
    </PageContainer>
  );
}
